"use client"

import axios from "axios"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import Delete from "../../../assets/svgs/Recycle Bin.svg"
import ConfirmationModalBov from "../../../components/ConfirmationModals/ConfirmationModalBov"
import useAuth from "../../../hooks/useAuth"
import { BASE_URL } from "../../../utils/Element"

const statusArray = {
  1: "new_lead",
  3: "executed_loi",
  4: "under_contract",
  5: "bov_presented",
  6: "exclusive_listing",
}

const StageDispositionDashboard = ({ activeTab, selectedUser }) => {
  const [loading, setLoading] = useState(true)
  const [leadId, setLeadId] = useState(null)
  const [config] = useAuth()
  const [showContractModal, setShowContractModal] = useState(false)
  const [categories, setCategories] = useState([
    { key: 1, title: "Qualified Prospect", pipelines: [] },
    { key: 5, title: "BOV Presented", pipelines: [] },
    { key: 6, title: "Exclusive Listing", pipelines: [] },
    { key: 3, title: "Executed LOI", pipelines: [] },
    { key: 4, title: "Under Contract", pipelines: [] },
  ])

  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showConfirmModalBov, setShowConfirmModalBov] = useState(false)
  const [showExcecutedLoi, setShowExcecutedLoi] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [pendingMove, setPendingMove] = useState(null)
  const [pipelineDetails, setPipelineDetails] = useState(null)

  // ✅ ADDED
  const [draggedPipelineId, setDraggedPipelineId] = useState(null)

  const fetchPipelineById = async (id) => {
    try {
      const response = await axios.get(`${BASE_URL}/getby-id-pipeline-global/${id}`, config)
      setPipelineDetails(response.data)
      return response.data
    } catch (error) {
      console.error("Error fetching pipeline by ID:", error)
      return null
    }
  }

  const PipelineStageAcquisition = async () => {
    try {
      setLoading(true)
      let url = `${BASE_URL}/dashboard-pipeline-stage-disposition?active_tab=${activeTab}`

      if (selectedUser && selectedUser.value) {
        url += `&show_user_id=${selectedUser.value}`
      }

      const response = await axios.get(url, config)
      const pipelineData = response?.data?.pipeline_stage?.pipeline_status_disposition_data || {}

      const formattedCategories = categories.map((cat) => {
        const statusKey = statusArray[cat.key]
        return {
          ...cat,
          pipelines: pipelineData[statusKey]
            ? pipelineData[statusKey].map((item) => ({
              id: item.id,
              name: item.contact_name,
              contact_id: item.contact_id,
              lastContacted: item.last_contact_update_date,
              potentialgcl: item.potential_gcl_format,
              pipeline: item.associate_name,
              lead_id: item.lead_id,
            }))
            : [],
        }
      })

      setCategories(formattedCategories)
    } catch (error) {
      console.error("Error fetching pipelines:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    PipelineStageAcquisition()
  }, [activeTab, selectedUser])

  const updatePipelineStatus = async (pipelineId, fromStage, toStage) => {
    try {
      await axios.put(
        `${BASE_URL}/dashboard-pipeline-stage-status/${pipelineId}`,
        { id: pipelineId, fromStage, toStage },
        config,
      )
    } catch (error) {
      console.error("Error updating pipeline status:", error)
      throw error
    }
  }

  const updatePipelineColumnOrder = async (pipelines) => {
    const columnUpdatePayload = pipelines.map((item, index) => ({
      id: item.id,
      column_no: index,
    }))

    try {
      await axios.put(`${BASE_URL}/pipeline-column-update`, JSON.stringify(columnUpdatePayload), {
        ...config,
        headers: { ...config.headers, "Content-Type": "application/json" },
      })
    } catch (error) {
      console.error("Error updating pipeline column order:", error)
      throw error
    }
  }

  // ⭐ KEEPING YOUR FUNCTION EXACTLY SAME — NOT TOUCHING
  const applyPendingMove = async () => {
    if (!pendingMove) return

    const { pipelineId, fromCategoryIndex, toCategoryIndex, insertIndex } = pendingMove
    const newCategories = [...categories]
    const fromPipelines = [...newCategories[fromCategoryIndex].pipelines]
    const draggedPipeline = fromPipelines.find((p) => p.id === pipelineId)

    if (!draggedPipeline) return

    fromPipelines.splice(
      fromPipelines.findIndex((p) => p.id === pipelineId),
      1,
    )
    const toPipelines = [...newCategories[toCategoryIndex].pipelines]
    toPipelines.splice(insertIndex, 0, draggedPipeline)

    newCategories[fromCategoryIndex].pipelines = fromPipelines
    newCategories[toCategoryIndex].pipelines = toPipelines
    setCategories(newCategories)

    try {
      const statusKeys = Object.keys(statusArray).map(Number)
      const fromStage = statusKeys[fromCategoryIndex]
      const toStage = statusKeys[toCategoryIndex]
      await updatePipelineStatus(pipelineId, fromStage, toStage)
      await updatePipelineColumnOrder(toPipelines)
    } catch (error) {
      await PipelineStageAcquisition()
    } finally {
      setPendingMove(null)
    }
  }

  const onDragStart = (e, pipelineId, fromCategoryIndex, pipelineIndex) => {
    setDraggedPipelineId(pipelineId)

    e.dataTransfer.setData("pipelineId", pipelineId)
    e.dataTransfer.setData("fromCategory", fromCategoryIndex)
    e.dataTransfer.setData("pipelineIndex", pipelineIndex)
  }

  const onDrop = (e, toCategoryIndex) => {
    e.preventDefault()
    const pipelineId = Number.parseInt(e.dataTransfer.getData("pipelineId"))
    const fromCategoryIndex = Number.parseInt(e.dataTransfer.getData("fromCategory"))
    const draggedIndex = Number.parseInt(e.dataTransfer.getData("pipelineIndex"))

    const dropY = e.clientY
    const container = e.currentTarget.querySelector(".pipeline-container")
    const children = Array.from(container?.children || [])
    let insertIndex = children.length

    for (let i = 0; i < children.length; i++) {
      const box = children[i].getBoundingClientRect()
      if (dropY < box.top + box.height / 2) {
        insertIndex = i
        break
      }
    }

    if (fromCategoryIndex === toCategoryIndex) {
      const newCategories = [...categories]
      const fromPipelines = [...newCategories[fromCategoryIndex].pipelines]
      const draggedPipeline = fromPipelines.splice(draggedIndex, 1)[0]
      fromPipelines.splice(insertIndex, 0, draggedPipeline)
      newCategories[toCategoryIndex].pipelines = fromPipelines
      setCategories(newCategories)
      updatePipelineColumnOrder(fromPipelines)
    } else {
      const fromStage = categories[fromCategoryIndex].key
      const toStage = categories[toCategoryIndex].key

      if (fromStage === 1 && toStage === 5) {
        setPendingMove({ pipelineId, fromCategoryIndex, toCategoryIndex, insertIndex })
        setSelectedId(pipelineId)
        fetchPipelineById(pipelineId)
        setShowConfirmModalBov(true)
      } else {
        const newCategories = [...categories]
        const fromPipelines = [...newCategories[fromCategoryIndex].pipelines]
        const draggedPipeline = fromPipelines.splice(draggedIndex, 1)[0]
        const toPipelines = [...newCategories[toCategoryIndex].pipelines]
        toPipelines.splice(insertIndex, 0, draggedPipeline)
        newCategories[fromCategoryIndex].pipelines = fromPipelines
        newCategories[toCategoryIndex].pipelines = toPipelines
        setCategories(newCategories)

        updatePipelineStatus(pipelineId, fromStage, toStage)
        updatePipelineColumnOrder(toPipelines)
      }
    }
  }

  const onDragOver = (e) => {
    e.preventDefault()
  }

  return (
    <div className="flex flex-col w-full h-screen bg-gray-100 p-4 overflow-hidden">

      {/* ===== PIPELINE COLUMNS ===== */}
      <div className="flex w-full space-x-3 flex-1 mb-4 overflow-y-auto">

        {categories.map((category, categoryIndex) => (
          <div
            key={categoryIndex}
            className="flex-1 flex flex-col"
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, categoryIndex)}
          >
            {/* Smaller Category Header */}
            <div className="green-bg-H text-white px-2 py-1 rounded-t-md text-center text-sm font-medium">
              {category.title}
            </div>

            {/* Scrollable Cards Area */}
            <div className="bg-white flex-1 overflow-y-auto border border-gray-200 rounded-b-md">

              {category.pipelines.map((pipeline, pipelineIndex) => (
                <div
                  key={pipeline.id}
                  className="p-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer text-sm"
                  draggable
                  onDragStart={(e) =>
                    onDragStart(e, pipeline.id, categoryIndex, pipelineIndex)
                  }
                >
                  <p className="font-medium text-sm">
                    <Link to={`/contact/${pipeline.contact_id}`}>
                      {pipeline.name}
                    </Link>
                  </p>

                  <p className="text-xs text-gray-500">
                    Last Contacted : {pipeline.lastContacted}
                  </p>
                  <p className="text-xs text-gray-500">
                    Potential GCI: ${pipeline.potentialgcl}
                  </p>
                  <p className="text-xs text-gray-500">
                    Pipeline: {pipeline.pipeline}
                  </p>
                </div>
              ))}

            </div>
          </div>
        ))}

      </div>

      {/* ===== DELETE DROP AREA — MINIMAL HEIGHT ===== */}
      <div
        className="w-full flex justify-center py-2"
        onDragOver={(e) => e.preventDefault()}
        onDrop={async () => {
          if (!draggedPipelineId) return
          try {
            await axios.delete(`${BASE_URL}/delete-pipeline/${draggedPipelineId}`, config)
            setDraggedPipelineId(null)
            PipelineStageAcquisition()
          } catch (err) {
            console.error("Delete failed", err)
          }
        }}
      >
        <button className="flex items-center gap-2 border border-red-400 text-red-500 px-4 py-1.5 rounded-full bg-white hover:bg-red-50 transition-all text-sm">
          <img src={Delete} alt="delete" className="w-4 h-4" />
          Remove from Pipeline
        </button>
      </div>

      <ConfirmationModalBov
        showModal={showConfirmModalBov}
        onClose={() => {
          setShowConfirmModalBov(false)
          setPendingMove(null)
        }}
        pipelineDetails={pipelineDetails}
        onCallApi={async () => {
          setShowConfirmModalBov(false)
          await applyPendingMove()
          PipelineStageAcquisition()
        }}
        id={selectedId}
      />
    </div>

  )
}

export default StageDispositionDashboard
